
import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import PageHero from '../components/common/PageHero';
import ServiceGrid from '../components/common/ServiceGrid';
import ProcessSteps from '../components/common/ProcessSteps';
import CTASection from '../components/common/CTASection';
import PlateformesSection from '../components/communication-digitale/PlateformesSection';
import ResultatsSection from '../components/communication-digitale/ResultatsSection';
import { Users, Smartphone, Globe, BarChart3, Target, Heart, Zap, TrendingUp } from 'lucide-react';

const CommunicationDigitalePage = () => {
  const services = [
    {
      icon: Smartphone,
      title: "Social Media Management",
      description: "Gestion complète de vos réseaux sociaux",
      features: ["Stratégie éditoriale", "Création de contenu", "Community management", "Publicité social media"]
    },
    {
      icon: Globe,
      title: "Content Marketing",
      description: "Création de contenus engageants et performants",
      features: ["Blog et articles", "Vidéos", "Infographies", "Podcasts"]
    },
    {
      icon: Target,
      title: "SEO/SEM",
      description: "Optimisation pour les moteurs de recherche",
      features: ["Référencement naturel", "Google Ads", "Audit SEO", "Stratégie de mots-clés"]
    },
    {
      icon: Heart,
      title: "Email Marketing",
      description: "Campagnes d'emailing personnalisées",
      features: ["Newsletters", "Automation", "Segmentation", "A/B Testing"]
    }
  ];

  const processSteps = [
    {
      step: "01",
      title: "Audit Digital",
      description: "Analyse de votre présence digitale actuelle et de vos performances",
      icon: BarChart3
    },
    {
      step: "02", 
      title: "Stratégie Digitale",
      description: "Définition des objectifs, cibles et canaux prioritaires",
      icon: Target
    },
    {
      step: "03",
      title: "Création & Production",
      description: "Développement des contenus et mise en place des campagnes",
      icon: Zap
    },
    {
      step: "04",
      title: "Optimisation Continue",
      description: "Analyse des performances et optimisation des actions",
      icon: TrendingUp
    }
  ];

  const tendances = [
    {
      icon: Smartphone,
      title: "Contenu Vidéo Court",
      description: "Reels, Stories, TikTok dominent l'engagement"
    },
    {
      icon: Users,
      title: "Social Commerce",
      description: "Achat direct sur les réseaux sociaux"
    },
    {
      icon: Target,
      title: "Micro-Ciblage",
      description: "Publicités ultra-personnalisées"
    },
    {
      icon: BarChart3,
      title: "Data-Driven",
      description: "Décisions basées sur l'analyse des données"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <PageHero
        badge={{ icon: Users, text: "Communication Digitale" }}
        title='Conquérez le <span class="font-semibold text-blue-600">digital</span>'
        subtitle="avec impact et créativité"
        description="Je booste votre présence digitale avec des stratégies data-driven et des contenus qui engagent. Réseaux sociaux, SEO, campagnes publicitaires : transformons vos objectifs en résultats mesurables."
      />

      {/* Pourquoi le digital */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-4xl font-light text-navy-900 mb-6">
                  Pourquoi le <span className="font-semibold text-blue-600">digital</span> est incontournable ?
                </h2>
                <p className="text-lg text-navy-600 font-light mb-6 leading-relaxed">
                  Avec 4.8 milliards d'utilisateurs actifs sur les réseaux sociaux et 93% des expériences en ligne qui commencent par un moteur de recherche, 
                  votre présence digitale n'est plus une option : c'est une nécessité stratégique.
                </p>
                <p className="text-lg text-navy-600 font-light mb-8 leading-relaxed">
                  Le digital offre une précision de ciblage, une mesure de performance et un ROI incomparables avec les médias traditionnels.
                </p>
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600 mb-2">4.8B</div>
                    <div className="text-sm text-navy-600">Utilisateurs réseaux sociaux</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-pink-500 mb-2">93%</div>
                    <div className="text-sm text-navy-600">Recherches commencent par Google</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600 mb-2">2.14B</div>
                    <div className="text-sm text-navy-600">Acheteurs e-commerce</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-pink-500 mb-2">6x</div>
                    <div className="text-sm text-navy-600">ROI moyen digital vs traditionnel</div>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-pink-50 p-12">
                <div className="text-center">
                  <Users className="text-blue-600 mx-auto mb-6" size={64} />
                  <h3 className="text-2xl font-semibold text-navy-900 mb-4">Votre audience vous attend</h3>
                  <p className="text-navy-600 font-light mb-6">
                    "Le digital n'est pas une tendance, c'est le présent et l'avenir de la communication."
                  </p>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-blue-600 text-white p-3 text-center">
                      <Smartphone size={20} className="mx-auto mb-1" />
                      <span className="text-xs">Mobile First</span>
                    </div>
                    <div className="bg-pink-500 text-white p-3 text-center">
                      <Target size={20} className="mx-auto mb-1" />
                      <span className="text-xs">Ciblage Précis</span>
                    </div>
                    <div className="bg-navy-700 text-white p-3 text-center">
                      <BarChart3 size={20} className="mx-auto mb-1" />
                      <span className="text-xs">Mesurable</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ServiceGrid
        title='Mes <span class="font-semibold text-blue-600">services</span> digitaux'
        subtitle="Une expertise complète pour dominer l'écosystème digital"
        services={services}
      />

      <PlateformesSection />

      <ProcessSteps
        title='Ma <span class="font-semibold text-blue-600">méthodologie</span> digitale'
        subtitle="Une approche data-driven pour des résultats mesurables"
        steps={processSteps}
      />

      <ResultatsSection />

      {/* Tendances */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-navy-900">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-light text-white mb-6">
              Tendances <span className="font-semibold text-pink-400">2024</span>
            </h2>
            <p className="text-xl text-navy-200 mb-12 font-light">
              Je reste à la pointe des innovations digitales
            </p>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {tendances.map((tendance, index) => (
                <div key={index} className="text-center bg-white/10 p-6 hover:bg-white/20 transition-colors duration-300">
                  <tendance.icon className="text-pink-400 mx-auto mb-4" size={32} />
                  <h3 className="text-lg font-semibold text-white mb-3">{tendance.title}</h3>
                  <p className="text-navy-200 font-light text-sm">{tendance.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CTASection
        title='Prêt à dominer le <span class="font-semibold text-blue-600">digital</span> ?'
        description="Transformons votre présence digitale en machine à générer des résultats."
        primaryButtonText="Lancer ma stratégie digitale"
        whatsappMessage="Bonjour Julycia ! J'aimerais discuter d'un projet de communication digitale avec vous."
      />

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default CommunicationDigitalePage;
